import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { 
  Camera, Upload, Wand2, Eye, Download, Trash2, 
  ArrowLeft, Play, Pause, Maximize2, Settings,
  Image as ImageIcon, Loader2, AlertCircle, Crown, RefreshCw,
  Share2, Copy, MessageCircle, Send
} from 'lucide-react';

const VirtualTourCreator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [images, setImages] = useState([]);
  const [tourName, setTourName] = useState('');
  const [tourDescription, setTourDescription] = useState('');
  const [style, setStyle] = useState('modern');
  const [transitionType, setTransitionType] = useState('fade');
  const [autoPlay, setAutoPlay] = useState(true);
  const [autoPlaySpeed, setAutoPlaySpeed] = useState(5);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedTour, setGeneratedTour] = useState(null);
  const [myTours, setMyTours] = useState([]);
  const [limits, setLimits] = useState(null);
  const [activeTab, setActiveTab] = useState('create');
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false, tour: null });

  // Get referral link from user
  const getReferralCode = () => user?.referral_code || user?.username || '';
  
  const getShareUrl = (tourId) => {
    const baseUrl = window.location.origin;
    const referralCode = getReferralCode();
    return `${baseUrl}/virtual-tour/${tourId}${referralCode ? `?ref=${referralCode}` : ''}`;
  };

  const handleShare = (tour, platform) => {
    const shareUrl = getShareUrl(tour.id);
    const shareText = `Check out this amazing virtual tour: "${tour.name}" - Created with ANANT Web Portal AI Tools!`;
    
    switch(platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`, '_blank');
        break;
      case 'telegram':
        window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied with your referral code!');
        break;
      default:
        break;
    }
    setShareModal({ open: false, tour: null });
  };

  useEffect(() => {
    if (user) {
      fetchLimits();
      fetchMyTours();
    }
  }, [user]);

  useEffect(() => {
    let interval;
    if (isPlaying && images.length > 1) {
      interval = setInterval(() => {
        setPreviewIndex(prev => (prev + 1) % images.length);
      }, autoPlaySpeed * 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, images.length, autoPlaySpeed]);

  const fetchLimits = async () => {
    try {
      const response = await api.get('/ai-tools/virtual-tour/limits');
      setLimits(response.data);
    } catch (error) {
      console.error('Error fetching limits:', error);
    }
  };

  const fetchMyTours = async () => {
    try {
      const response = await api.get('/ai-tools/virtual-tour/my-tours');
      setMyTours(response.data.tours || []);
    } catch (error) {
      console.error('Error fetching tours:', error);
    }
  };

  const handleImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    const maxImages = limits?.limits?.max_images_per_tour || 20;
    
    if (images.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          data: e.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
  }, [images.length, limits]);

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const handleGenerate = async () => {
    if (!user) {
      toast.error('Please login to create virtual tours');
      navigate('/login');
      return;
    }

    if (images.length < 3) {
      toast.error('Please upload at least 3 images');
      return;
    }

    if (!tourName.trim()) {
      toast.error('Please enter a tour name');
      return;
    }

    if (!window.confirm('AI Virtual Tour par 20 AT deduct hoga. Continue?')) return;

    setIsGenerating(true);
    try {
      const response = await api.post('/ai-tools/virtual-tour/create', {
        name: tourName,
        description: tourDescription,
        images: images.map(img => img.data),
        style,
        transition_type: transitionType,
        auto_play: autoPlay,
        auto_play_speed: autoPlaySpeed
      });

      setGeneratedTour(response.data);
      toast.success('Virtual tour created successfully!');
      fetchLimits();
      fetchMyTours();
      setActiveTab('preview');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create virtual tour');
    } finally {
      setIsGenerating(false);
    }
  };

  const deleteTour = async (tourId) => {
    if (!window.confirm('Are you sure you want to delete this tour?')) return;
    
    try {
      await api.delete(`/ai-tools/virtual-tour/${tourId}`);
      toast.success('Tour deleted');
      fetchMyTours();
    } catch (error) {
      toast.error('Failed to delete tour');
    }
  };

  const styles = [
    { value: 'modern', label: 'Modern', desc: 'Clean, minimalist design' },
    { value: 'classic', label: 'Classic', desc: 'Traditional, elegant style' },
    { value: 'minimalist', label: 'Minimalist', desc: 'Simple and focused' }
  ];

  const transitions = [
    { value: 'fade', label: 'Fade', desc: 'Smooth fade between images' },
    { value: 'slide', label: 'Slide', desc: 'Slide transitions' },
    { value: 'zoom', label: 'Zoom', desc: 'Zoom in/out effects' }
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="glass-card p-8 max-w-md text-center">
          <Camera className="w-16 h-16 mx-auto mb-4 text-primary" />
          <h2 className="text-2xl font-bold mb-4">Virtual Tour Creator</h2>
          <p className="text-muted-foreground mb-6">
            Create stunning 360° virtual tours from your photos using AI
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary w-full"
            data-testid="login-btn"
          >
            Login to Get Started
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="virtual-tour-creator">
      {/* Hero */}
      <div className="bg-gradient-to-br from-cyan-500/20 via-background to-blue-500/10 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <button 
            onClick={() => navigate('/tools')}
            className="flex items-center gap-2 text-muted-foreground hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </button>
          
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold">Virtual Tour Creator</h1>
                  <p className="text-sm text-muted-foreground">AI-Powered 360° Tours</p>
                </div>
              </div>
            </div>
            
            {limits && (
              <div className="glass-card px-4 py-2 flex items-center gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-primary">{limits.remaining}</div>
                  <div className="text-xs text-muted-foreground">Tours Left</div>
                </div>
                <div className="h-8 w-px bg-border"></div>
                <div className="text-center">
                  <div className="text-lg font-bold">{limits.limits?.max_images_per_tour || 20}</div>
                  <div className="text-xs text-muted-foreground">Max Images</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 mt-6">
        <div className="flex gap-2 border-b border-border">
          {[
            { id: 'create', label: 'Create Tour', icon: Wand2 },
            { id: 'my-tours', label: 'My Tours', icon: Eye },
            { id: 'preview', label: 'Preview', icon: Play, disabled: !generatedTour }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-white'
              } ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {activeTab === 'create' && (
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Upload Section */}
            <div className="lg:col-span-3 space-y-6">
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-primary" />
                  Upload Images
                </h3>
                
                {/* Upload Area */}
                <label className="block border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-upload-input"
                  />
                  <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">Drop images here or click to upload</p>
                  <p className="text-sm text-muted-foreground">
                    Upload 3-{limits?.limits?.max_images_per_tour || 20} images (JPG, PNG, WebP)
                  </p>
                </label>

                {/* Image Preview Grid */}
                {images.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">{images.length} images uploaded</span>
                      <button
                        onClick={() => setImages([])}
                        className="text-sm text-red-500 hover:text-red-400"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {images.map((img, index) => (
                        <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden">
                          <img
                            src={img.data}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                              onClick={() => removeImage(img.id)}
                              className="p-2 bg-red-500 rounded-full"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="absolute top-2 left-2 bg-black/70 px-2 py-0.5 rounded text-xs">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {images.length > 0 && images.length < 3 && (
                  <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <p className="text-sm text-yellow-500">
                      Please upload at least 3 images to create a virtual tour
                    </p>
                  </div>
                )}
              </div>

              {/* Tour Details */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-primary" />
                  Tour Details
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Tour Name *</label>
                    <input
                      type="text"
                      value={tourName}
                      onChange={(e) => setTourName(e.target.value)}
                      placeholder="e.g., My Dream Home Tour"
                      className="input-field w-full"
                      data-testid="tour-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <textarea
                      value={tourDescription}
                      onChange={(e) => setTourDescription(e.target.value)}
                      placeholder="Describe your virtual tour..."
                      className="input-field w-full h-24 resize-none"
                      data-testid="tour-description-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Settings & Generate */}
            <div className="lg:col-span-2 space-y-6">
              {/* Style Selection */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4">Tour Style</h3>
                <div className="space-y-3">
                  {styles.map(s => (
                    <label
                      key={s.value}
                      className={`block p-3 rounded-lg border cursor-pointer transition-colors ${
                        style === s.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-white/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="style"
                        value={s.value}
                        checked={style === s.value}
                        onChange={(e) => setStyle(e.target.value)}
                        className="hidden"
                      />
                      <div className="font-medium">{s.label}</div>
                      <div className="text-sm text-muted-foreground">{s.desc}</div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Transition */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold mb-4">Transition Effect</h3>
                <div className="grid grid-cols-3 gap-2">
                  {transitions.map(t => (
                    <button
                      key={t.value}
                      onClick={() => setTransitionType(t.value)}
                      className={`p-3 rounded-lg border text-center transition-colors ${
                        transitionType === t.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-white/30'
                      }`}
                    >
                      <div className="font-medium text-sm">{t.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Auto Play */}
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Auto Play</h3>
                  <button
                    onClick={() => setAutoPlay(!autoPlay)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      autoPlay ? 'bg-primary' : 'bg-border'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      autoPlay ? 'translate-x-6' : 'translate-x-0.5'
                    }`}></div>
                  </button>
                </div>
                
                {autoPlay && (
                  <div>
                    <label className="block text-sm mb-2">Speed: {autoPlaySpeed}s</label>
                    <input
                      type="range"
                      min="2"
                      max="10"
                      value={autoPlaySpeed}
                      onChange={(e) => setAutoPlaySpeed(Number(e.target.value))}
                      className="w-full"
                    />
                  </div>
                )}
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={isGenerating || images.length < 3 || !tourName.trim()}
                className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="generate-tour-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating Tour...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    Generate Virtual Tour
                  </>
                )}
              </button>

              {limits?.remaining === 0 && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-500 mb-2">
                    <Crown className="w-5 h-5" />
                    <span className="font-semibold">Limit Reached</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    You've used all your monthly tours. Upgrade your plan for more!
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'my-tours' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">My Virtual Tours ({myTours.length})</h2>
              <button onClick={fetchMyTours} className="btn-secondary flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {myTours.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <Camera className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">No tours yet</h3>
                <p className="text-muted-foreground mb-4">Create your first virtual tour!</p>
                <button onClick={() => setActiveTab('create')} className="btn-primary">
                  Create Tour
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myTours.map(tour => (
                  <div key={tour.id} className="glass-card overflow-hidden group">
                    <div className="aspect-video bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center relative">
                      {tour.original_images?.[0] ? (
                        <img 
                          src={tour.original_images[0]} 
                          alt={tour.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Camera className="w-12 h-12 text-muted-foreground" />
                      )}
                      <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded text-xs">
                        {tour.status === 'completed' ? 'Ready' : 'Processing'}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold mb-1 truncate">{tour.name}</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        {tour.original_images?.length || 0} images • {tour.views || 0} views
                      </p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => window.open(`/virtual-tour/${tour.id}`, '_blank')}
                          className="btn-secondary flex-1 flex items-center justify-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        <button 
                          onClick={() => setShareModal({ open: true, tour })}
                          className="p-2 text-green-500 hover:bg-green-500/10 rounded-lg"
                          title="Share with referral link"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => deleteTour(tour.id)}
                          className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'preview' && generatedTour && (
          <div className="max-w-4xl mx-auto">
            <div className="glass-card overflow-hidden">
              {/* Preview Area */}
              <div className="aspect-video bg-black relative">
                {images[previewIndex] && (
                  <img
                    src={images[previewIndex].data}
                    alt={`Scene ${previewIndex + 1}`}
                    className={`w-full h-full object-contain transition-all duration-500 ${
                      transitionType === 'fade' ? 'animate-fade-in' :
                      transitionType === 'zoom' ? 'animate-zoom-in' : ''
                    }`}
                  />
                )}
                
                {/* Controls Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsPlaying(!isPlaying)}
                        className="p-2 bg-white/20 rounded-full hover:bg-white/30"
                      >
                        {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                      </button>
                      <span className="text-sm">{previewIndex + 1} / {images.length}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 bg-white/20 rounded-full hover:bg-white/30">
                        <Download className="w-5 h-5" />
                      </button>
                      <button className="p-2 bg-white/20 rounded-full hover:bg-white/30">
                        <Maximize2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Thumbnail Strip */}
                  <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {images.map((img, index) => (
                      <button
                        key={img.id}
                        onClick={() => {
                          setPreviewIndex(index);
                          setIsPlaying(false);
                        }}
                        className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-colors ${
                          index === previewIndex ? 'border-primary' : 'border-transparent'
                        }`}
                      >
                        <img src={img.data} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Info */}
              <div className="p-6">
                <h2 className="text-xl font-bold mb-2">{tourName}</h2>
                <p className="text-muted-foreground mb-4">{tourDescription || 'No description'}</p>
                
                <div className="flex flex-wrap gap-3">
                  <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                    {style} style
                  </span>
                  <span className="px-3 py-1 bg-cyan-500/20 text-cyan-500 rounded-full text-sm">
                    {transitionType} transition
                  </span>
                  <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm">
                    {images.length} scenes
                  </span>
                </div>

                {generatedTour.status === 'completed' && (
                  <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-500 font-medium">
                      Tour generated successfully! AI has enhanced your images.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {shareModal.open && shareModal.tour && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShareModal({ open: false, tour: null })}>
          <div className="glass-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="share-modal">
            <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Share Virtual Tour
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Share "{shareModal.tour.name}" with your referral link embedded!
            </p>
            
            {/* Referral Code Badge */}
            <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg mb-4">
              <p className="text-xs text-muted-foreground mb-1">Your Referral Code</p>
              <p className="font-mono font-bold text-primary">{getReferralCode() || 'Not available'}</p>
            </div>

            {/* Share Link Preview */}
            <div className="p-3 bg-white/5 rounded-lg mb-4">
              <p className="text-xs text-muted-foreground mb-1">Share Link</p>
              <p className="text-sm font-mono break-all">{getShareUrl(shareModal.tour.id)}</p>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => handleShare(shareModal.tour, 'whatsapp')}
                className="flex items-center justify-center gap-2 p-3 bg-green-500/20 text-green-500 rounded-xl hover:bg-green-500/30 transition-colors"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </button>
              <button
                onClick={() => handleShare(shareModal.tour, 'telegram')}
                className="flex items-center justify-center gap-2 p-3 bg-blue-500/20 text-blue-500 rounded-xl hover:bg-blue-500/30 transition-colors"
              >
                <Send className="w-5 h-5" />
                Telegram
              </button>
              <button
                onClick={() => handleShare(shareModal.tour, 'facebook')}
                className="flex items-center justify-center gap-2 p-3 bg-blue-600/20 text-blue-400 rounded-xl hover:bg-blue-600/30 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                Facebook
              </button>
              <button
                onClick={() => handleShare(shareModal.tour, 'twitter')}
                className="flex items-center justify-center gap-2 p-3 bg-sky-500/20 text-sky-500 rounded-xl hover:bg-sky-500/30 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                Twitter/X
              </button>
            </div>

            <button
              onClick={() => handleShare(shareModal.tour, 'copy')}
              className="w-full flex items-center justify-center gap-2 p-3 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors"
              data-testid="copy-link-btn"
            >
              <Copy className="w-5 h-5" />
              Copy Link with Referral
            </button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              When someone visits via your link and joins, you earn commissions!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VirtualTourCreator;
